import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Bell, BellOff, Loader2 } from "lucide-react";

// urlBase64ToUint8Array — required to convert VAPID public key for PushManager.subscribe()
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) output[i] = rawData.charCodeAt(i);
  return output;
}

// Subscribes the browser to push notifications via the public service worker.
// Falls back gracefully when push or service workers aren't supported.
const PushSubscribeButton = ({ token }) => {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    const supportsPush = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(supportsPush);
    if (!supportsPush) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/notifications/push-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscribed(res.data.subscribed);
      } catch {
        setSubscribed(false);
      }
    })();
  }, [token]);

  const enable = async () => {
    if (!supported) return;
    setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        toast.error("Notification permission denied");
        setBusy(false);
        return;
      }
      const reg = await navigator.serviceWorker.register("/push-sw.js");
      // Wait for SW to be ready
      await navigator.serviceWorker.ready;

      // Fetch VAPID public key
      let applicationServerKey;
      try {
        const vapidRes = await axios.get(`${API}/notifications/vapid-key`);
        if (vapidRes.data?.public_key) {
          applicationServerKey = urlBase64ToUint8Array(vapidRes.data.public_key);
        }
      } catch (e) {
        console.warn("VAPID key fetch failed; falling back to permission-only", e);
      }

      let subscription = await reg.pushManager.getSubscription();
      if (!subscription) {
        try {
          subscription = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            ...(applicationServerKey ? { applicationServerKey } : {})
          });
        } catch (e) {
          console.warn("PushManager.subscribe failed", e);
          subscription = null;
        }
      }

      const subJson = subscription?.toJSON?.();
      await axios.post(`${API}/notifications/subscribe-push`, {
        endpoint: subJson?.endpoint || subscription?.endpoint || `local:${navigator.userAgent.slice(0, 40)}`,
        keys: subJson?.keys || {}
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribed(true);
      toast.success("Push notifications enabled");
    } catch (e) {
      console.error(e);
      toast.error("Could not enable push notifications");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration("/push-sw.js");
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) await sub.unsubscribe();
      await axios.post(`${API}/notifications/unsubscribe-push`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribed(false);
      toast.success("Push notifications disabled");
    } catch {
      toast.error("Could not disable push notifications");
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <div className="text-xs text-[#4A5260]" data-testid="push-unsupported">
        Push notifications aren't supported by this browser.
      </div>
    );
  }

  return subscribed ? (
    <button
      onClick={disable}
      disabled={busy}
      className="px-4 py-2 rounded-lg bg-[#1A2030] text-[#8A92A0] hover:bg-[#2A3040] flex items-center gap-2 text-sm disabled:opacity-60"
      data-testid="push-disable-btn"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellOff className="w-4 h-4" />}
      Disable push notifications
    </button>
  ) : (
    <button
      onClick={enable}
      disabled={busy}
      className="px-4 py-2 rounded-lg bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] flex items-center gap-2 text-sm font-semibold disabled:opacity-60"
      data-testid="push-enable-btn"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
      Enable push notifications
    </button>
  );
};

export default PushSubscribeButton;
